import { ErrorBoundary } from "@sentry/react"
import { Modal as AModal } from "antd"
import { type ReactNode, useEffect, useState } from "react"

import ErrorFallbackModal from "#blocks/ErrorFallbackModal"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { type ReactChild } from "#customTypes/globalTypes"
import useAnalytics from "#hooks/useAnalytics"

const Modal = ({
  content,
  title,
  open,
  onOk,
  okText,
  onClose,
  children,
  width,
  className,
  footer,
  modalName,
  loading,
}: {
  /**
   * Use content or children, but not both
   */
  content?: ReactNode
  title: string | ReactNode
  open?: boolean
  onOk?: (data: unknown) => void
  okText?: string
  onClose?: () => void
  children?: ReactChild
  width?: number
  className?: string
  footer?: ReactNode | null
  modalName?: string
  loading?: boolean
}) => {
  const [isOpen, setIsOpen] = useState(open ?? false)
  const { trackModalAction } = useAnalytics()

  const closeModal = () => {
    if (onClose) {
      onClose()
    } else {
      setIsOpen(false)
    }
  }

  // Track modal open/close
  useEffect(() => {
    const isModalOpen = open ?? isOpen
    if (isModalOpen && modalName) {
      trackModalAction(ANALYTICS_EVENTS.MODAL_OPENED, { modalName })
    }

    return () => {
      if (isModalOpen && modalName) {
        trackModalAction(ANALYTICS_EVENTS.MODAL_CLOSED, { modalName })
      }
    }
  }, [open, isOpen, modalName, trackModalAction])

  if (!open && !isOpen) return null

  return (
    <ErrorBoundary
      fallback={({ error }) => (
        <ErrorFallbackModal
          title="Oops! Something went wrong"
          error={error as Error}
        />
      )}
    >
      <AModal
        forceRender
        title={title}
        open={open ?? isOpen}
        onOk={onOk}
        okText={okText}
        onCancel={closeModal}
        width={width}
        className={className}
        footer={footer}
        destroyOnClose
        style={{
          // TODO: Calculate height of content vs page height
          top: 30,
        }}
        confirmLoading={loading}
      >
        {content ?? children}
      </AModal>
    </ErrorBoundary>
  )
}

export default Modal
